// components/PromoLabelChips.jsx
//
// Position-aware. Labels sharing the same `position` are grouped and
// rendered together at that spot (or inline). Wrap this in a
// `position: relative` container around your product image for the
// non-inline variants to anchor correctly — see PRODUCT_CARD_USAGE below.

const POSITION_CLASSES = {
  'top-left': 'absolute top-7 left-1 items-start',
  'top-right': 'absolute top-7 right-1 items-end',
  'top-center': 'absolute top-1 left-1/2 -translate-x-1/2 items-center w-95/100',
  'bottom-left': 'absolute bottom-7 left-1 items-start',
  'bottom-right': 'absolute bottom-7 right-1 items-end',
  'bottom-center': 'absolute bottom-0 left-1/2 -translate-x-1/2 items-center w-95/100',
  'inline': 'static items-start' // not absolutely positioned — flows in normal layout
};

// Used only when a label has no bg_color of its own (no badge assigned,
// or the badge left color/bg_color blank) — keyed by promo_type so a
// bare bogo/bundle/etc. still looks reasonable without any admin setup.
const FALLBACK_BG = {
  bogo: '#16A34A',
  // green
  bundle: '#2563EB',
  // blue
  percentage: '#111827',
  // near-black
  fixed: '#111827',
  volume: '#111827'
};
function chipStyle(l) {
  const bg = l.bg_color || FALLBACK_BG[l.promo_type] || '#111827';
  const text = l.color || '#ffffff';
  if (l.style === 'outline') {
    return {
      backgroundColor: 'rgba(255,255,255,0.8)',
      color: bg,
      // use the bg color as the outline/text color so it still reads as "that badge's color"
      border: `1.5px solid ${bg}`
    };
  }

  // 'solid' and 'ribbon' both render as a filled chip today — 'ribbon'
  // is a lightweight approximation (shadow + slight skew) rather than a
  // true clipped-corner ribbon shape, to avoid extra markup/CSS for now.
  return {
    backgroundColor: bg,
    color: text,
    ...(l.style === 'ribbon' ? {
      transform: 'skewX(-4deg)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
    } : {})
  };
}

/**
 * Shape rules, driven by the GROUP's position (not the individual badge):
 *  - top-center / bottom-center render as a full-width bar, not a pill.
 *  - bottom-center: no rounding at all, on any badge in the stack.
 *  - top-center: only the FIRST badge in the stack gets rounded top
 *    corners (it's the one flush against the image's top edge); any
 *    badges stacked below it stay square, so the group reads as one
 *    continuous banner rather than a pile of separately-rounded pills.
 *  - every other position (corners, inline) keeps the normal pill shape.
 */
function chipShapeClasses(position, isFirst) {
  if (position === 'bottom-center') {
    return 'w-full text-center';
  }
  if (position === 'top-center') {
    return `w-full text-center ${isFirst ? 'rounded' : ''}`;
  }
  return 'w-fit rounded leading-tight';
}
export default function PromoLabelChips({
  labels = []
}) {
  if (!labels.length) return null;
  const groups = labels.reduce((acc, l) => {
    const pos = l.position || 'top-left';
    (acc[pos] ||= []).push(l);
    return acc;
  }, {});
  return <>
            {Object.entries(groups).map(([position, group]) => <div key={position} className={`flex flex-col gap-1 z-2 ${POSITION_CLASSES[position] ?? POSITION_CLASSES['top-left']}`}>
                    {group.map((l_0, idx) => <span key={`${l_0.kind}-${l_0.id}`} className={`text-xs font-semibold px-2 py-0.5 ${chipShapeClasses(position, idx === 0)}`} style={chipStyle(l_0)}>
                            {l_0.label}
                        </span>)}
                </div>)}
        </>;
}

/**
 * PRODUCT_CARD_USAGE — the wrapping container needs position:relative
 * for the non-inline variants to anchor against the image, not the page:
 *
 *   <div className="relative">
 *     <Image src={product.featured_image} ... />
 *     <PromoLabelChips labels={labelsForProduct(labelMap, product.product_id, product.cat_ids)} />
 *   </div>
 *
 * 'inline' position labels render in normal document flow instead —
 * put PromoLabelChips outside the relative image wrapper (e.g. below
 * the price) if you want a merchant's 'inline' choice to actually flow
 * inline rather than sit invisibly at static position:relative-less
 * top-left. If a product has both an overlay position and 'inline' at
 * once, render PromoLabelChips twice — once inside the image wrapper,
 * once outside — and let each call's `labels` filter by position:
 *
 *   const overlay = labels.filter(l => l.position !== 'inline');
 *   const inline = labels.filter(l => l.position === 'inline');
 */